/**
 * ProfilePage Component
 *
 * User profile page for viewing and editing personal information.
 * Includes session management for viewing and revoking active sessions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { CopyableCode } from '../components/ui/CopyableCode';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { useAuth } from '../contexts/AuthContext';
import { useLayoutProps } from '../hooks/useLayoutProps';
import { formatDate, formatRelativeTime, getDeviceDescription } from '../lib/utils/format';
import { userService } from '../api/user.service';
import { authService } from '../api/auth.service';
import type { SessionSanitized } from '../types/auth.types';

export const ProfilePage: React.FC = () => {
  const { user, tenant, token, updateUser } = useAuth();
  const layoutProps = useLayoutProps();
  const [tokenCopied, setTokenCopied] = useState(false);

  // Edit profile modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  // Session management state
  const [sessions, setSessions] = useState<SessionSanitized[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [sessionToRevoke, setSessionToRevoke] = useState<SessionSanitized | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);
  const [isRevokeAllModalOpen, setIsRevokeAllModalOpen] = useState(false);

  // Load sessions on mount
  const loadSessions = useCallback(async () => {
    setIsLoadingSessions(true);
    setSessionsError(null);
    try {
      const response = await authService.listSessions();
      setSessions(response);
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Failed to load sessions';
      setSessionsError(message);
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  if (!user || !tenant) {
    return null;
  }

  const handleCopyToken = async () => {
    if (token) {
      await navigator.clipboard.writeText(token);
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    }
  };

  const handleOpenEditModal = () => {
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setUpdateError(null);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setUpdateError(null);
  };

  const handleSaveProfile = async () => {
    // Validate inputs
    if (!firstName.trim() || !lastName.trim()) {
      setUpdateError('First name and last name are required');
      return;
    }

    // Check if anything changed
    if (firstName === user.firstName && lastName === user.lastName) {
      handleCloseEditModal();
      return;
    }

    setIsUpdating(true);
    setUpdateError(null);

    try {
      const updatedUser = await userService.updateSelf(tenant.tenantId, user.userId, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });

      // Update the user in AuthContext
      updateUser(updatedUser);
      handleCloseEditModal();
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Failed to update profile';
      setUpdateError(message);
    } finally {
      setIsUpdating(false);
    }
  };

  // Session management handlers
  const handleOpenRevokeModal = (session: SessionSanitized) => {
    setSessionToRevoke(session);
    setIsRevokeModalOpen(true);
  };

  const handleCloseRevokeModal = () => {
    setIsRevokeModalOpen(false);
    setSessionToRevoke(null);
  };

  const handleRevokeSession = async () => {
    if (!sessionToRevoke) return;

    setIsRevoking(true);
    try {
      await authService.revokeSession(sessionToRevoke.sessionId);
      // Remove from local state
      setSessions(prev => prev.filter(s => s.sessionId !== sessionToRevoke.sessionId));
      handleCloseRevokeModal();
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Failed to revoke session';
      setSessionsError(message);
    } finally {
      setIsRevoking(false);
    }
  };

  const handleOpenRevokeAllModal = () => {
    setIsRevokeAllModalOpen(true);
  };

  const handleCloseRevokeAllModal = () => {
    setIsRevokeAllModalOpen(false);
  };

  const handleRevokeAllSessions = async () => {
    setIsRevoking(true);
    try {
      await authService.revokeAllSessions(true); // Keep current session
      // Reload sessions to show only current
      await loadSessions();
      handleCloseRevokeAllModal();
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Failed to revoke sessions';
      setSessionsError(message);
    } finally {
      setIsRevoking(false);
    }
  };

  const otherSessionsCount = sessions.filter(s => !s.isCurrent).length;

  const fullName = `${user.firstName} ${user.lastName}`;
  const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || '?';

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Profile</h1>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            Manage your personal information and preferences
          </p>
        </div>

        {/* Profile Card */}
        <Card>
          <Card.Body>
            <div className="space-y-6">
              {/* Avatar and Basic Info */}
              <div className="flex items-center gap-6 pb-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
                <div className="h-20 w-20 rounded-full bg-primary-600 flex items-center justify-center text-white font-bold text-2xl">
                  {initials}
                </div>
                <div>
                  <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{fullName}</h2>
                  <p style={{ color: 'var(--color-text-secondary)' }}>{user.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {user.isAdmin && (
                      <Badge variant="info">Admin</Badge>
                    )}
                    <Badge variant="success">Active</Badge>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
                  Personal Information
                </h3>
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>First Name</dt>
                    <dd className="mt-1 text-sm" style={{ color: 'var(--color-text-primary)' }}>{user.firstName}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Last Name</dt>
                    <dd className="mt-1 text-sm" style={{ color: 'var(--color-text-primary)' }}>{user.lastName}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Email Address</dt>
                    <dd className="mt-1 text-sm" style={{ color: 'var(--color-text-primary)' }}>{user.email}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>User ID</dt>
                    <dd className="mt-1">
                      <CopyableCode value={user.userId} />
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Organization Information */}
              <div className="pt-6 border-t" style={{ borderColor: 'var(--color-border)' }}>
                <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
                  Organization
                </h3>
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Organization Name</dt>
                    <dd className="mt-1 text-sm" style={{ color: 'var(--color-text-primary)' }}>{tenant.tenantName}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Organization Code</dt>
                    <dd className="mt-1 text-sm" style={{ color: 'var(--color-text-primary)' }}>{tenant.tenantCode}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Tenant ID</dt>
                    <dd className="mt-1">
                      <CopyableCode value={tenant.tenantId} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Member Since</dt>
                    <dd className="mt-1 text-sm" style={{ color: 'var(--color-text-primary)' }}>
                      {formatDate(user.createdAt)}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Account Actions */}
              <div className="pt-6 border-t" style={{ borderColor: 'var(--color-border)' }}>
                <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
                  Account Actions
                </h3>
                <div className="flex gap-4">
                  <Button variant="secondary" onClick={handleOpenEditModal}>
                    Edit Profile
                  </Button>
                  <Button variant="secondary" disabled>
                    Change Password
                  </Button>
                </div>
                <p className="mt-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                  Password change feature coming soon.
                </p>
              </div>

              {/* Developer Tools */}
              <div className="pt-6 border-t" style={{ borderColor: 'var(--color-border)' }}>
                <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
                  Developer Tools
                </h3>
                <div className="flex items-center gap-4">
                  <Button
                    variant={tokenCopied ? 'success' : 'secondary'}
                    onClick={handleCopyToken}
                    disabled={!token}
                  >
                    {tokenCopied ? 'Token Copied!' : 'Copy Auth Token'}
                  </Button>
                </div>
                <p className="mt-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                  Copy your authentication token for use with the CLI or API.
                </p>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Active Sessions Card */}
        <Card>
          <Card.Body>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    Active Sessions
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    Manage your active sessions across devices
                  </p>
                </div>
                {otherSessionsCount > 0 && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleOpenRevokeAllModal}
                  >
                    Sign out all other devices
                  </Button>
                )}
              </div>

              {/* Sessions Error */}
              {sessionsError && (
                <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg">
                  {sessionsError}
                  <button
                    className="ml-2 text-red-800 underline"
                    onClick={() => setSessionsError(null)}
                  >
                    Dismiss
                  </button>
                </div>
              )}

              {/* Sessions Loading */}
              {isLoadingSessions && (
                <div className="py-8 text-center" style={{ color: 'var(--color-text-muted)' }}>
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                  Loading sessions...
                </div>
              )}

              {/* Sessions List */}
              {!isLoadingSessions && sessions.length > 0 && (
                <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
                  {sessions.map((session) => (
                    <div
                      key={session.sessionId}
                      className="py-4 flex items-center justify-between"
                    >
                      <div className="flex items-start gap-3">
                        {/* Device Icon */}
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-sunken)' }}>
                          <svg
                            className="w-5 h-5"
                            style={{ color: 'var(--color-text-secondary)' }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                        </div>

                        {/* Session Details */}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                              {getDeviceDescription(session.deviceInfo?.userAgent)}
                            </span>
                            {session.isCurrent && (
                              <Badge variant="success" size="sm">
                                Current
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
                            {session.deviceInfo?.ip && (
                              <span>IP: {session.deviceInfo.ip}</span>
                            )}
                          </div>
                          <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                            Last active: {formatRelativeTime(session.lastActivityAt)}
                            <span className="mx-2">-</span>
                            Created: {formatDate(session.createdAt)}
                          </div>
                        </div>
                      </div>

                      {/* Revoke Button */}
                      {!session.isCurrent && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleOpenRevokeModal(session)}
                        >
                          Revoke
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Empty State */}
              {!isLoadingSessions && sessions.length === 0 && !sessionsError && (
                <div className="py-8 text-center" style={{ color: 'var(--color-text-muted)' }}>
                  No active sessions found.
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Edit Profile Modal */}
      <Modal isOpen={isEditModalOpen} onClose={handleCloseEditModal} size="sm">
        <Modal.Header title="Edit Profile" subtitle="Update your personal information" />
        <Modal.Body>
          <div className="space-y-4">
            {updateError && (
              <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg">
                {updateError}
              </div>
            )}
            <Input
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter your first name"
              required
              disabled={isUpdating}
            />
            <Input
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Enter your last name"
              required
              disabled={isUpdating}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseEditModal}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveProfile}
            disabled={isUpdating}
          >
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Revoke Single Session Modal */}
      <Modal isOpen={isRevokeModalOpen} onClose={handleCloseRevokeModal} size="sm">
        <Modal.Header
          title="Revoke Session"
          subtitle="Are you sure you want to sign out this device?"
        />
        <Modal.Body>
          {sessionToRevoke && (
            <div className="space-y-3">
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg-sunken)' }}>
                <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                  {getDeviceDescription(sessionToRevoke.deviceInfo?.userAgent)}
                </p>
                {sessionToRevoke.deviceInfo?.ip && (
                  <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
                    IP: {sessionToRevoke.deviceInfo.ip}
                  </p>
                )}
                <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
                  Last active: {formatRelativeTime(sessionToRevoke.lastActivityAt)}
                </p>
              </div>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                This will immediately sign out the user on that device. They will
                need to log in again to continue using the application.
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseRevokeModal}
            disabled={isRevoking}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleRevokeSession}
            disabled={isRevoking}
          >
            {isRevoking ? 'Revoking...' : 'Revoke Session'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Revoke All Sessions Modal */}
      <Modal isOpen={isRevokeAllModalOpen} onClose={handleCloseRevokeAllModal} size="sm">
        <Modal.Header
          title="Sign Out All Other Devices"
          subtitle="Are you sure you want to sign out all other devices?"
        />
        <Modal.Body>
          <div className="space-y-3">
            <p style={{ color: 'var(--color-text-secondary)' }}>
              This will immediately sign out {otherSessionsCount} other{' '}
              {otherSessionsCount === 1 ? 'session' : 'sessions'}. You will remain
              signed in on this device.
            </p>
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
              Use this if you suspect unauthorized access to your account or if
              you forgot to sign out on a shared computer.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseRevokeAllModal}
            disabled={isRevoking}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleRevokeAllSessions}
            disabled={isRevoking}
          >
            {isRevoking ? 'Signing out...' : 'Sign Out All Other Devices'}
          </Button>
        </Modal.Footer>
      </Modal>
    </DashboardLayout>
  );
};

export default ProfilePage;
